import config from 'src/shared/config';
import {
    AgentAttachment,
    AgentConversation,
    AgentDraft,
    AgentDraftEditResponse,
    AgentGesture,
    AgentMessageRequest,
    AgentTurnRequest,
    DeleteAgentPrintAreaInput,
    SetAgentSideScaleInput,
    ReplaceAgentSideImageInput,
    UpsertAgentPrintAreaInput
} from './types';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 180_000;

export class AgentApiError extends Error {
    constructor(
        message: string,
        public readonly status: number
    ) {
        super(message);
        this.name = 'AgentApiError';
    }
}

const apiUrl = (path: string) => `${config.apiUrl?.replace(/\/$/, '') ?? ''}/${path.replace(/^\//, '')}`;

const readErrorMessage = async (response: Response) => {
    const fallback = `${response.status} ${response.statusText}`.trim();
    const contentType = response.headers.get('content-type') ?? '';

    try {
        if (contentType.includes('application/json')) {
            const body = (await response.json()) as { message?: string; detail?: string; title?: string };
            return body.detail || body.message || body.title || fallback;
        }

        return (await response.text()).slice(0, 300) || fallback;
    } catch {
        return fallback;
    }
};

const request = async <T>(path: string, init: RequestInit = {}, allowNotFound = false): Promise<T | null> => {
    const response = await fetch(apiUrl(path), {
        ...init,
        headers: {
            Authorization: `Bearer ${config.token}`,
            Accept: 'application/json',
            'Accept-Language': config.culture,
            ...(init.body ? { 'Content-Type': 'application/json' } : {}),
            ...init.headers
        }
    });

    if (allowNotFound && response.status === 404) return null;

    if (!response.ok) {
        throw new AgentApiError(await readErrorMessage(response), response.status);
    }

    if (response.status === 204) return null;
    return (await response.json()) as T;
};

export const createAgentMessageIdempotencyKey = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createAgentConversation = async (name: string, signal?: AbortSignal) => {
    return (await request<AgentConversation>('v1/agent/conversations', {
        method: 'POST',
        body: JSON.stringify({ name }),
        signal
    })) as AgentConversation;
};

export const getAgentConversation = async (conversationId: number, signal?: AbortSignal) => {
    return request<AgentConversation>(`v1/agent/conversations/${conversationId}`, { signal }, true);
};

export const getAgentDraft = async (conversationId: number, signal?: AbortSignal) => {
    return request<AgentDraft>(`v1/agent/conversations/${conversationId}/draft`, { signal }, true);
};

export const upsertAgentPrintArea = async (conversationId: number, input: UpsertAgentPrintAreaInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/print-areas`, {
        method: 'POST',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const replaceAgentSideImage = async (conversationId: number, input: ReplaceAgentSideImageInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/side-image`, {
        method: 'PUT',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const deleteAgentPrintArea = async (conversationId: number, input: DeleteAgentPrintAreaInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/print-areas`, {
        method: 'DELETE',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const restoreAgentPrintArea = async (conversationId: number, input: DeleteAgentPrintAreaInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/print-areas/restore`, {
        method: 'POST',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const setAgentSideScale = async (conversationId: number, input: SetAgentSideScaleInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/side-scale`, {
        method: 'PUT',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const postAgentMessage = async (
    conversationId: number,
    message: string,
    attachments: AgentAttachment[],
    gesture: AgentGesture | null,
    idempotencyKey: string = createAgentMessageIdempotencyKey(),
    signal?: AbortSignal
) => {
    const body: AgentMessageRequest = {
        message: message || null,
        attachments: attachments.length > 0 ? attachments : null,
        gesture
    };

    return (await request<AgentTurnRequest>(`v1/agent/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
        signal
    })) as AgentTurnRequest;
};

const wait = (milliseconds: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        const complete = () => {
            signal?.removeEventListener('abort', abort);
            resolve();
        };
        const timeoutId = window.setTimeout(complete, milliseconds);
        const abort = () => {
            window.clearTimeout(timeoutId);
            reject(new DOMException('The operation was aborted.', 'AbortError'));
        };

        if (signal?.aborted) abort();
        else signal?.addEventListener('abort', abort, { once: true });
    });

export const waitForAgentTurn = async (conversationId: number, requestId: number, signal?: AbortSignal) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
        await wait(POLL_INTERVAL_MS, signal);
        const turnRequest = (await request<AgentTurnRequest>(`v1/agent/conversations/${conversationId}/requests/${requestId}`, { signal })) as AgentTurnRequest;
        if (turnRequest.state === 'completed' || turnRequest.state === 'failed') return turnRequest;
    }

    throw new AgentApiError('The request is taking longer than expected and may still complete.', 408);
};
